<template>
  <div>
    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
    </div>

    <!-- No strategy yet -->
    <div v-else-if="!strategy" class="text-center py-12">
      <h3 class="text-lg font-heading font-semibold text-gray-900 dark:text-gray-100 mb-2">
        AI Keyword Strategy
      </h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Generate a comprehensive keyword universe with competitor cross-referencing.
      </p>
      <div
        v-if="benchmarkConfidence && !benchmarkConfidence.quality_profile_approved"
        class="max-w-xl mx-auto mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-left">
        <p class="text-xs text-amber-800 dark:text-amber-300">
          Approve a reliable competitor quality profile before generating keyword strategy.
        </p>
        <p v-for="warning in benchmarkConfidence.warnings || []" :key="warning" class="text-xs text-amber-700 dark:text-amber-400 mt-1">
          {{ warning }}
        </p>
      </div>
      <p v-if="runError" class="text-sm text-red-600 dark:text-red-400 mb-4">
        {{ runError }}
      </p>
      <button @click="handleRun" :disabled="benchmarkConfidence && !benchmarkConfidence.quality_profile_approved" class="btn btn-primary disabled:opacity-50">
        Generate Keyword Strategy
      </button>
    </div>

    <!-- Running -->
    <div v-else-if="strategy.status !== 'completed' && strategy.status !== 'failed'" class="text-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4" />
      <p class="text-sm text-gray-600 dark:text-gray-400">
        {{ progressMessage || `Processing (${strategy.status})...` }}
      </p>
      <button @click="handleRun" class="btn btn-secondary btn-sm mt-4">
        Re-run Strategy
      </button>
    </div>

    <!-- Failed -->
    <div v-else-if="strategy.status === 'failed'" class="text-center py-12">
      <p class="text-sm text-red-600 dark:text-red-400 mb-4">
        {{ strategy.error_message }}
      </p>
      <button @click="handleRun" class="btn btn-primary">
        Retry
      </button>
    </div>

    <!-- Completed: Step 1 — Universe Approval -->
    <div v-else-if="!strategy.keyword_universe?.approved" class="space-y-6">
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-heading font-semibold text-gray-900 dark:text-gray-100">
          Keyword Universe ({{ strategy.items?.length || 0 }} keywords)
        </h3>
        <button @click="handleApproveUniverse" class="btn btn-primary btn-sm">
          Approve Universe
        </button>
      </div>

      <form class="p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50" @submit.prevent="addManualUniverseKeyword">
        <label for="manual-strategy-keyword" class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
          Add manual keyword
        </label>
        <div class="flex flex-col sm:flex-row gap-2">
          <input
            id="manual-strategy-keyword"
            v-model="manualUniverseKeyword"
            type="text"
            placeholder="Keyword to include in the strategy"
            class="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
          <select
            v-model="manualUniverseTier"
            class="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
            <option v-for="tier in tiers" :key="tier.key" :value="tier.key">
              {{ tier.label }}
            </option>
          </select>
          <button type="submit" class="btn btn-secondary btn-sm">
            Add
          </button>
        </div>
        <div v-if="addedKeywords.length" class="mt-3 flex flex-wrap gap-2">
          <span
            v-for="keyword in addedKeywords"
            :key="`${keyword.keyword}-${keyword.tier}`"
            class="inline-flex items-center gap-2 px-2 py-1 rounded-lg bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs">
            {{ keyword.keyword }}
            <button type="button" class="text-primary-500 hover:text-red-500" @click="removeManualUniverseKeyword(keyword)">
              Remove
            </button>
          </span>
        </div>
      </form>

      <!-- Tier sections -->
      <div v-for="tier in tiers" :key="tier.key" class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
        <button @click="toggleTier(tier.key)" class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800">
          <span class="font-medium text-gray-900 dark:text-gray-100">
            {{ tier.label }} ({{ tierItems(tier.key).length }})
          </span>
          <span class="text-gray-400">{{ expandedTiers[tier.key] ? '−' : '+' }}</span>
        </button>
        <div v-if="expandedTiers[tier.key]" class="p-4">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-900/50">
                <th class="pb-2">
                  Keyword
                </th>
                <th class="pb-2">
                  Volume
                </th>
                <th class="pb-2">
                  CPC
                </th>
                <th class="pb-2">
                  Source
                </th>
                <th class="pb-2" />
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in tierItems(tier.key)" :key="item.id" class="border-t border-gray-100 dark:border-gray-800">
                <td class="py-2 text-gray-900 dark:text-gray-100">
                  {{ item.keyword }}
                  <div v-if="item.flag_pills?.length" class="flex flex-wrap gap-1 mt-1">
                    <span
                      v-for="pill in item.flag_pills"
                      :key="pill.key"
                      :class="pillClass(pill.tone)"
                      class="px-2 py-0.5 rounded-lg text-xs">
                      {{ pill.label }}
                    </span>
                  </div>
                  <p v-if="variantKeywords(item).length" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Variants counted with this keyword: {{ variantKeywords(item).join(', ') }}
                  </p>
                </td>
                <td class="py-2 text-gray-600 dark:text-gray-400">
                  {{ formatVolume(item) }}
                </td>
                <td class="py-2 text-gray-600 dark:text-gray-400">
                  {{ formatCpc(item) }}
                </td>
                <td class="py-2">
                  <span class="px-2 py-0.5 rounded-lg text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {{ item.source }}
                  </span>
                  <span
                    v-if="item.metrics_status?.status === 'unavailable'"
                    class="ml-1 px-2 py-0.5 rounded-lg text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                    Data unavailable
                  </span>
                </td>
                <td class="py-2 text-right">
                  <button @click="removeItem(item.id)" class="text-red-400 hover:text-red-600 text-xs">
                    Remove
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <KeywordStrategyExcludedItems
        :items="universeExcludedItems"
        :variant-keywords="variantKeywords"
        description="Competitor brands, job searches, malformed fragments, unverified claims and keywords owned by another page default to Exclude. Malformed fragments are hidden from the tiers above and never counted; the others stay listed above so you can review them." />
    </div>

    <!-- Completed: Step 2 — Bucket Cross-Reference -->
    <div v-else-if="!strategy.approved" class="space-y-6">
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-heading font-semibold text-gray-900 dark:text-gray-100">
          Cross-Reference Results
        </h3>
        <button @click="handleApproveCluster" class="btn btn-primary btn-sm">
          Approve Final Cluster
        </button>
      </div>

      <div
        v-if="strategy.benchmark_confidence?.warnings?.length"
        class="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
        <h4 class="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">
          Competitor Data Reliability
        </h4>
        <p v-for="warning in strategy.benchmark_confidence.warnings" :key="warning" class="text-sm text-amber-700 dark:text-amber-400">
          {{ warning }}
        </p>
      </div>

      <!-- Cannibalization warnings -->
      <div v-if="strategy.cannibalization_flags?.length" class="rounded-xl bg-red-50 dark:bg-red-900/20 p-4">
        <h4 class="text-sm font-medium text-red-800 dark:text-red-300 mb-2">
          Cannibalization Risks
        </h4>
        <div v-for="flag in strategy.cannibalization_flags" :key="flag.item_id || flag.keyword" class="text-sm text-red-700 dark:text-red-400 py-0.5">
          "{{ flag.keyword }}" overlaps {{ flag.sibling_page }} ({{ flag.sibling_slug }})
          <span v-if="flag.level" class="ml-1 px-2 py-0.5 rounded-lg text-xs bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
            {{ flag.level === 'strong' ? 'Strong overlap' : 'Review' }}
          </span>
          <span v-if="flag.recommendation" class="ml-1 text-xs font-medium">— {{ flag.recommendation }}</span>
        </div>
      </div>

      <div
        v-for="bucket in bucketSections"
        :key="bucket.key"
        :class="bucket.borderClass"
        class="rounded-xl p-4">
        <h4 :class="bucket.headingClass" class="text-sm font-medium mb-3">
          {{ bucket.label }} ({{ bucketItems(bucket.key).length }})
        </h4>
        <div v-if="bucketItems(bucket.key).length === 0" class="text-sm text-gray-500 dark:text-gray-400">
          No keywords in this bucket.
        </div>
        <div v-for="item in bucketItems(bucket.key)" :key="item.id" class="flex items-center justify-between gap-3 py-2 border-t border-gray-100 dark:border-gray-800 first:border-0">
          <div>
            <span class="text-sm text-gray-900 dark:text-gray-100">{{ item.keyword }}</span>
            <p v-if="item.recommendation_reasoning" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {{ item.recommendation_reasoning }}
            </p>
            <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              Default: {{ recommendationLabel(item) }}
              <span v-if="item.metrics_status?.status === 'unavailable'"> · Keyword data unavailable</span>
            </p>
            <span :class="validationBadge(item.validation_status)" class="inline-flex mt-1 px-2 py-0.5 rounded-lg text-xs">
              {{ validationLabel(item.validation_status) }}
            </span>
            <div v-if="item.flag_pills?.length" class="flex flex-wrap gap-1 mt-1">
              <span
                v-for="pill in item.flag_pills"
                :key="pill.key"
                :class="pillClass(pill.tone)"
                class="px-2 py-0.5 rounded-lg text-xs">
                {{ pill.label }}
              </span>
            </div>
            <p v-if="variantKeywords(item).length" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Variants counted with this keyword: {{ variantKeywords(item).join(', ') }}
            </p>
          </div>
          <select
            :value="decisionSelectValue(item)"
            @change="handleItemDecision(item.id, $event.target.value)"
            class="text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-800">
            <option value="included">
              Include{{ defaultDecision(item) === 'included' ? ' (default)' : '' }}
            </option>
            <option value="excluded">
              Exclude{{ defaultDecision(item) === 'excluded' ? ' (default)' : '' }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Approved — show strategy summary -->
    <div v-else class="space-y-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span class="text-green-500">&#10003;</span>
          <h3 class="text-lg font-heading font-semibold text-gray-900 dark:text-gray-100">
            Keyword Strategy ({{ approvedItems.length }} keywords)
          </h3>
        </div>
        <button @click="handleRun" class="btn btn-secondary btn-sm">
          Re-run Strategy
        </button>
      </div>

      <!-- Tier sections (read-only) -->
      <div v-for="tier in tiers" :key="tier.key">
        <div
          v-if="approvedTierItems(tier.key).length"
          class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
          <button @click="toggleTier(tier.key)" class="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800">
            <span class="font-medium text-gray-900 dark:text-gray-100">
              {{ tier.label }} ({{ approvedTierItems(tier.key).length }})
            </span>
            <span class="text-gray-400">{{ expandedTiers[tier.key] ? '−' : '+' }}</span>
          </button>
          <div v-if="expandedTiers[tier.key]" class="p-4">
            <table class="w-full text-sm">
              <thead>
                <tr class="text-left text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-900/50">
                  <th class="pb-2">
                    Keyword
                  </th>
                  <th class="pb-2">
                    Volume
                  </th>
                  <th class="pb-2">
                    CPC
                  </th>
                  <th class="pb-2">
                    Bucket
                  </th>
                  <th class="pb-2">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in approvedTierItems(tier.key)" :key="item.id" class="border-t border-gray-100 dark:border-gray-800">
                  <td class="py-2 text-gray-900 dark:text-gray-100">
                    {{ item.keyword }}
                    <p v-if="variantKeywords(item).length" class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Variants: {{ variantKeywords(item).join(', ') }}
                    </p>
                  </td>
                  <td class="py-2 text-gray-600 dark:text-gray-400">
                    {{ formatVolume(item) }}
                  </td>
                  <td class="py-2 text-gray-600 dark:text-gray-400">
                    {{ formatCpc(item) }}
                  </td>
                  <td class="py-2">
                    <span v-if="item.bucket" :class="bucketClass(item.bucket)" class="px-2 py-0.5 rounded-lg text-xs">
                      {{ bucketLabel(item.bucket) }}
                    </span>
                  </td>
                  <td class="py-2">
                    <span class="px-2 py-0.5 rounded-lg text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      {{ item.source }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Cannibalization warnings if any -->
      <div v-if="strategy.cannibalization_flags?.length" class="rounded-xl bg-red-50 dark:bg-red-900/20 p-4">
        <h4 class="text-sm font-medium text-red-800 dark:text-red-300 mb-2">
          Cannibalization Risks
        </h4>
        <div v-for="flag in strategy.cannibalization_flags" :key="flag.item_id || flag.keyword" class="text-sm text-red-700 dark:text-red-400 py-0.5">
          "{{ flag.keyword }}" overlaps {{ flag.sibling_page }} ({{ flag.sibling_slug }})
          <span v-if="flag.level" class="ml-1 px-2 py-0.5 rounded-lg text-xs bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
            {{ flag.level === 'strong' ? 'Strong overlap' : 'Review' }}
          </span>
          <span v-if="flag.recommendation" class="ml-1 text-xs font-medium">— {{ flag.recommendation }}</span>
        </div>
      </div>

      <KeywordStrategyExcludedItems
        :items="approvedExcludedItems"
        :variant-keywords="variantKeywords"
        description="These keywords were excluded from the approved strategy (by quality checks or during review) and are not counted or targeted." />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import consumer from '~/channels/consumer'
import KeywordStrategyExcludedItems from './keyword_strategy_excluded_items.vue'
import { runKeywordStrategy, getKeywordStrategy, approveKeywordUniverse, approveKeywordCluster } from '~/javascript/api/keyword_strategy.js'

const props = defineProps({
  pageId: { type: String, required: true }
})

const emit = defineEmits(['approved'])

let subscription = null
let pollTimer = null

const loading = ref(true)
const strategy = ref(null)
const benchmarkConfidence = ref(null)
const progressMessage = ref('')
const runError = ref('')
const expandedTiers = ref({ core: true, high_value: true, supporting: false, long_tail: false })
const itemDecisions = ref({})
const removedIds = ref([])
const addedKeywords = ref([])
const manualUniverseKeyword = ref('')
const manualUniverseTier = ref('supporting')

const tiers = [
  { key: 'core', label: 'Core Keywords' },
  { key: 'high_value', label: 'High-Value Secondary' },
  { key: 'supporting', label: 'Supporting Keywords' },
  { key: 'long_tail', label: 'Long-Tail Keywords' }
]

const bucketSections = [
  {
    key: 'a',
    label: 'Bucket A — Shared Keywords',
    borderClass: 'border border-primary-200 dark:border-primary-800',
    headingClass: 'text-primary-700 dark:text-primary-400'
  },
  {
    key: 'b',
    label: 'Bucket B — Potential Gap Candidates',
    borderClass: 'border border-green-200 dark:border-green-800',
    headingClass: 'text-green-700 dark:text-green-400'
  },
  {
    key: 'c',
    label: 'Bucket C — Competitor-Only',
    borderClass: 'border border-amber-200 dark:border-amber-800',
    headingClass: 'text-amber-700 dark:text-amber-400'
  }
]

const isIncludedItem = (item) => {
  const decision = item.user_decision
  if (decision === 'included') return true
  if (decision === 'excluded') return false
  return item.include_recommendation === 'include' || item.include_recommendation == null
}

const allItems = computed(() => strategy.value?.items || [])
const itemIds = computed(() => new Set(allItems.value.map((i) => i.id)))
const isMalformed = (item) => Boolean(item.flags?.malformed) && item.user_decision !== 'included'
// Concept variants are listed under their canonical keyword, not as separate rows.
const isGroupedVariant = (item) => item.canonical === false && itemIds.value.has(item.flags?.variant_of)
const variantKeywords = (item) => {
  if (item.variants?.length) return item.variants
  return allItems.value.filter((i) => i.canonical === false && i.flags?.variant_of === item.id).map((i) => i.keyword)
}
// Universe step: items the quality checks default to Exclude (brand, jobs,
// malformed, unverified claims, owned elsewhere).
const universeExcludedItems = computed(() => allItems.value.filter((i) => (
  !removedIds.value.includes(i.id) && !isGroupedVariant(i) && (isMalformed(i) || i.default_decision === 'exclude')
)))
const tierItems = (tier) => allItems.value.filter((i) => (
  i.tier === tier && !removedIds.value.includes(i.id) && !isMalformed(i) && !isGroupedVariant(i)
))
const approvedItems = computed(() => allItems.value.filter((i) => isIncludedItem(i)))
const approvedTierItems = (tier) => approvedItems.value.filter((i) => i.tier === tier && !isGroupedVariant(i))
// Approved view: everything not in the final strategy, so excluded items don't silently vanish.
const approvedExcludedItems = computed(() => allItems.value.filter((i) => !isIncludedItem(i) && !isGroupedVariant(i)))

const pillClass = (tone) => {
  const map = {
    danger: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    warning: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    info: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
  }
  return map[tone] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
}
const bucketItems = (bucket) => (strategy.value?.items || []).filter((i) => i.bucket === bucket)
const toggleTier = (key) => { expandedTiers.value[key] = !expandedTiers.value[key] }

const bucketClass = (bucket) => {
  const map = {
    a: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300',
    b: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    c: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
  }
  return map[bucket] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
}

const bucketLabel = (bucket) => {
  const map = { a: 'Shared', b: 'Potential', c: 'Competitor' }
  return map[bucket] || bucket
}

const removeItem = (id) => { removedIds.value.push(id) }

const handleItemDecision = (itemId, decision) => {
  itemDecisions.value[itemId] = decision || null
}

// Default select value comes from the serialized recommendation (brand,
// jobs, malformed, unverified claims and owned-elsewhere items default to Exclude).
const defaultDecision = (item) => {
  if (item.default_decision) return item.default_decision === 'include' ? 'included' : 'excluded'
  return isIncludedItem(item) ? 'included' : 'excluded'
}

const decisionSelectValue = (item) => {
  if (Object.prototype.hasOwnProperty.call(itemDecisions.value, item.id) && itemDecisions.value[item.id]) {
    return itemDecisions.value[item.id]
  }

  return item.user_decision || defaultDecision(item)
}

const recommendationLabel = (item) => {
  if (item.include_recommendation === 'include') return 'Must use'
  if (item.include_recommendation === 'exclude') return 'Exclude'
  if (item.include_recommendation === 'evaluate') return 'Evaluate manually'
  return 'Include'
}

const formatVolume = (item) => {
  if (item.metrics_status?.status === 'unavailable') return 'Data unavailable'
  if (item.search_volume == null) return 'Data unavailable'
  return Number(item.search_volume).toLocaleString()
}

const formatCpc = (item) => {
  if (item.metrics_status?.status === 'unavailable') return 'Data unavailable'
  if (item.cpc == null) return 'Data unavailable'
  return `$${Number(item.cpc).toFixed(2)}`
}

const validationLabel = (status) => {
  const map = {
    validated: 'Validated',
    needs_review: 'Needs review',
    remove: 'Remove'
  }
  return map[status] || 'Needs review'
}

const validationBadge = (status) => {
  const map = {
    validated: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    needs_review: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    remove: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
  }
  return map[status] || map.needs_review
}

const addManualUniverseKeyword = () => {
  const keyword = manualUniverseKeyword.value.trim()
  if (!keyword) return
  const exists = addedKeywords.value.some((item) => item.keyword.toLowerCase() === keyword.toLowerCase()) ||
    (strategy.value?.items || []).some((item) => item.keyword.toLowerCase() === keyword.toLowerCase())
  if (!exists) {
    addedKeywords.value.push({ keyword, tier: manualUniverseTier.value })
  }
  manualUniverseKeyword.value = ''
}

const removeManualUniverseKeyword = (keyword) => {
  addedKeywords.value = addedKeywords.value.filter((item) => item !== keyword)
}

const stopPolling = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const fetchStrategy = async() => {
  try {
    loading.value = true
    const response = await getKeywordStrategy(props.pageId)
    strategy.value = response.data.keyword_strategy
    benchmarkConfidence.value = response.data.benchmark_confidence || strategy.value?.benchmark_confidence || null
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error fetching strategy:', e)
  } finally {
    loading.value = false
  }
}

const startPolling = () => {
  stopPolling()
  pollTimer = setInterval(async() => {
    try {
      const response = await getKeywordStrategy(props.pageId)
      const s = response.data.keyword_strategy
      benchmarkConfidence.value = response.data.benchmark_confidence || s?.benchmark_confidence || benchmarkConfidence.value
      if (s && (s.status === 'completed' || s.status === 'failed')) {
        strategy.value = s
        stopPolling()
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

const subscribeToChannel = () => {
  if (subscription) subscription.unsubscribe()
  subscription = consumer.subscriptions.create(
    { channel: 'KeywordStrategyChannel', page_id: props.pageId },
    {
      received(data) {
        progressMessage.value = data.message || ''
        if (data.step === 'completed' || data.step === 'failed') {
          stopPolling()
          fetchStrategy()
        }
      }
    }
  )
}

const handleRun = async() => {
  try {
    runError.value = ''
    await runKeywordStrategy(props.pageId)
    subscribeToChannel()
    strategy.value = { status: 'generating' }
    loading.value = false
    startPolling()
  } catch (e) {
    runError.value = e.response?.data?.message || 'Failed to start keyword strategy.'
    benchmarkConfidence.value = e.response?.data?.benchmark_confidence || benchmarkConfidence.value
    // eslint-disable-next-line no-console
    console.error('Failed to start strategy:', e)
  }
}

const handleApproveUniverse = async() => {
  try {
    const response = await approveKeywordUniverse(props.pageId, {
      addedKeywords: addedKeywords.value,
      removedItemIds: removedIds.value
    })
    strategy.value = response.data.keyword_strategy
    addedKeywords.value = []
    removedIds.value = []
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to approve universe:', e)
  }
}

const handleApproveCluster = async() => {
  try {
    const response = await approveKeywordCluster(props.pageId, itemDecisions.value)
    strategy.value = response.data.keyword_strategy
    emit('approved')
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to approve cluster:', e)
  }
}

onMounted(async() => {
  await fetchStrategy()
  subscribeToChannel()
  // If strategy is in a non-terminal state, start polling
  if (strategy.value && strategy.value.status !== 'completed' && strategy.value.status !== 'failed') {
    startPolling()
  }
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
